import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CommonScrollBar from "../../Common/CommonScrollBar";
import CommonDateTime from "../../Common/CommonDateTime";
import { WARRANTY_PLANS_URL, WARRANTY_PLAN_DRAFT_URL } from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import ChangesTable from "../common/ChangesTable";
import {
  comparePlanVersions,
  createPlanDraft,
  getPlanVersion,
  listPlanVersions,
  listProductGroups,
} from "../common/warrantyApi";
import { requestIdFor, settleRequestId } from "../common/clientRequest";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { PlaceholderBadge, StateBadge } from "./PlansHome";
import VersionContentView from "./VersionContentView";
import { websiteModelsFrom } from "./editor/draftModel";
import "../warranty.css";

const olderFirst = (a, b) =>
  a.effectiveFrom.localeCompare(b.effectiveFrom) || a.createdAt.localeCompare(b.createdAt);

const PlanVersionHistory = () => {
  const { roomId: tier } = useParams();
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const navigate = useNavigate();
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.PLANS_ADMIN);
  useWarrantyNav(allowed);

  const [list, setList] = useState({ status: "loading", items: [], message: "" });
  const [selected, setSelected] = useState([]);
  const [compare, setCompare] = useState({ status: "idle", data: null, message: "" });
  const [viewing, setViewing] = useState({ open: false, id: "", status: "loading", version: null, message: "" });
  const [busyId, setBusyId] = useState("");
  const [rowErrors, setRowErrors] = useState({});
  // The website models, to name a version's bike segment models; empty until (or unless) loaded.
  const [websiteModels, setWebsiteModels] = useState([]);
  const mounted = useRef(true);
  const createIdRef = useRef(null);
  const compareSeq = useRef(0);
  const viewSeq = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setList((prev) => ({ ...prev, status: "loading" }));
    const result = await listPlanVersions(accessToken, tier);
    if (!mounted.current) {
      return;
    }
    if (result.ok) {
      setList({ status: "ready", items: result.data.items || [], message: "" });
    } else {
      setList({ status: "error", items: [], message: result.message });
    }
  }, [accessToken, tier]);

  useEffect(() => {
    if (allowed) {
      load();
    }
  }, [allowed, load]);

  const toggle = (id) => {
    setCompare({ status: "idle", data: null, message: "" });
    compareSeq.current += 1;
    setSelected((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return prev.length >= 2 ? [prev[1], id] : [...prev, id];
    });
  };

  const runCompare = async () => {
    const chosen = list.items.filter((item) => selected.includes(item.id)).sort(olderFirst);
    if (chosen.length !== 2) {
      return;
    }
    const seq = ++compareSeq.current;
    setCompare({ status: "loading", data: null, message: "" });
    const result = await comparePlanVersions(accessToken, chosen[0].id, chosen[1].id);
    if (!mounted.current || seq !== compareSeq.current) {
      return;
    }
    if (result.ok) {
      setCompare({ status: "ready", data: result.data, message: "" });
    } else {
      setCompare({ status: "error", data: null, message: result.message });
    }
  };

  // Once per screen, best effort: without it a segment's models are shown by their slugs.
  useEffect(() => {
    if (!allowed) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const result = await listProductGroups(accessToken);
      if (!cancelled && mounted.current && result.ok) {
        setWebsiteModels(websiteModelsFrom(result.data));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, allowed]);

  const openVersion = async (id) => {
    const seq = ++viewSeq.current;
    setViewing({ open: true, id, status: "loading", version: null, message: "" });
    const result = await getPlanVersion(accessToken, id);
    // Closed, or another version opened since: this answer is no longer wanted.
    if (!mounted.current || seq !== viewSeq.current) {
      return;
    }
    if (result.ok) {
      setViewing({ open: true, id, status: "ready", version: result.data, message: "" });
    } else {
      setViewing({ open: true, id, status: "error", version: null, message: result.message });
    }
  };

  const newDraftFrom = async (version) => {
    setBusyId(version.id);
    setRowErrors((prev) => ({ ...prev, [version.id]: null }));
    const body = { tier, basedOnPlanVersionId: version.id };
    const result = await createPlanDraft(
      accessToken,
      body,
      requestIdFor(createIdRef, `from:${version.id}`)
    );
    settleRequestId(createIdRef, result);
    if (!mounted.current) {
      return;
    }
    setBusyId("");
    if (result.ok) {
      navigate(`${WARRANTY_PLAN_DRAFT_URL}/${encodeURIComponent(result.data.id)}`);
    } else {
      setRowErrors((prev) => ({
        ...prev,
        [version.id]: { message: result.message, draftId: result.details?.draftId },
      }));
    }
  };

  if (!allowed) {
    return (
      <section className="Main_section">
        <div className="warranty-page">
          <EmptyState title="You do not have access to Assure plans">
            <span>Ask an admin to give you the Warranty Assure Plans permission.</span>
          </EmptyState>
        </div>
      </section>
    );
  }

  const chosen = list.items.filter((item) => selected.includes(item.id)).sort(olderFirst);
  const displayName = list.items.length > 0 ? list.items[0].displayName : tier;

  return (
    <section className="Main_section">
      <CommonScrollBar className={"ScrollbarsSidebar"} height={{ height: "calc(100vh - 90px)" }}>
        <div className="warranty-page">
          <div className="warranty-page-head">
            <div>
              <h4>{displayName} version history</h4>
              <p className="warranty-sub">Tier {tier}, market IN. Newest first.</p>
            </div>
            <div className="warranty-page-actions">
              <Link className="warranty-inline-btn" to={WARRANTY_PLANS_URL}>
                Back to plans
              </Link>
              <Button
                className="Addbtn"
                onClick={runCompare}
                disabled={selected.length !== 2 || compare.status === "loading"}
                title={selected.length === 2 ? "Compare the two chosen versions" : "Tick two versions to compare"}
              >
                {compare.status === "loading" ? "Comparing" : "Compare"}
              </Button>
            </div>
          </div>

          {list.status === "loading" && list.items.length === 0 && <Loading label="Loading versions" />}
          {list.status === "error" && <ErrorState message={list.message} onRetry={load} />}
          {list.status === "ready" && list.items.length === 0 && (
            <EmptyState title="This tier has no versions yet" />
          )}
          {list.items.length > 0 && (
            <div className="tableView warranty-history-table">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Compare</th>
                    <th scope="col">Terms</th>
                    <th scope="col">State</th>
                    <th scope="col">Starts</th>
                    <th scope="col">Created</th>
                    <th scope="col">Cancelled</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((version) => (
                    <React.Fragment key={version.id}>
                      <tr>
                        <td>
                          <Form.Check
                            type="checkbox"
                            id={`warranty-compare-${version.id}`}
                            aria-label={`Choose ${version.termsVersion} starting ${version.effectiveFrom} to compare`}
                            checked={selected.includes(version.id)}
                            onChange={() => toggle(version.id)}
                          />
                        </td>
                        <td>
                          {version.termsVersion}{" "}
                          {version.placeholderTerms && <PlaceholderBadge />}
                        </td>
                        <td>
                          <StateBadge state={version.state} />
                        </td>
                        <td>{CommonDateTime(version.effectiveFrom, "LL", "show")}</td>
                        <td>{CommonDateTime(version.createdAt, "LL, h:mm A", "show")}</td>
                        <td>{CommonDateTime(version.cancelledAt, "LL, h:mm A", "show")}</td>
                        <td>
                          <button
                            type="button"
                            className="warranty-inline-btn me-2"
                            onClick={() => openVersion(version.id)}
                            aria-label={`View ${version.termsVersion} starting ${version.effectiveFrom}`}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="warranty-inline-btn is-primary"
                            onClick={() => newDraftFrom(version)}
                            disabled={busyId !== ""}
                          >
                            {busyId === version.id ? "Starting" : "New draft from this version"}
                          </button>
                        </td>
                      </tr>
                      {rowErrors[version.id] && (
                        <tr>
                          <td colSpan={7} style={{ whiteSpace: "normal" }}>
                            <span className="warranty-field-msg is-error" role="alert" style={{ float: "none" }}>
                              {rowErrors[version.id].message}{" "}
                              {rowErrors[version.id].draftId && (
                                <Link to={`${WARRANTY_PLAN_DRAFT_URL}/${encodeURIComponent(rowErrors[version.id].draftId)}`}>
                                  Open that draft
                                </Link>
                              )}
                            </span>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {compare.status !== "idle" && (
            <div className="warranty-section" style={{ marginTop: 16 }} aria-live="polite">
              <h5>
                {chosen.length === 2
                  ? `Changes from ${chosen[0].termsVersion} (${CommonDateTime(chosen[0].effectiveFrom, "LL", "show")}) to ${chosen[1].termsVersion} (${CommonDateTime(chosen[1].effectiveFrom, "LL", "show")})`
                  : "Changes"}
              </h5>
              {compare.status === "loading" && <Loading label="Comparing" />}
              {compare.status === "error" && <ErrorState message={compare.message} onRetry={runCompare} />}
              {compare.status === "ready" && (
                <ChangesTable changes={compare.data.changes} emptyText="These two versions are the same." />
              )}
            </div>
          )}
        </div>
      </CommonScrollBar>

      <Modal
        show={viewing.open}
        onHide={() => {
          viewSeq.current += 1;
          setViewing({ open: false, id: "", status: "loading", version: null, message: "" });
        }}
        className="commonModal"
        dialogClassName="warranty-publish-dialog"
        scrollable
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {viewing.version ? `${viewing.version.displayName}, ${viewing.version.termsVersion}` : "Plan version"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewing.status === "loading" && <Loading label="Loading the version" />}
          {viewing.status === "error" && (
            <ErrorState message={viewing.message} onRetry={() => openVersion(viewing.id)} />
          )}
          {viewing.status === "ready" && <VersionContentView version={viewing.version} models={websiteModels} />}
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default PlanVersionHistory;
